import { useEffect } from 'react';

const DEFAULT_OG_IMAGE = '/og/home.png';
const BLOG_OG_IMAGE = '/og/blog.png';
// Pages with their own rendered card under /public/og (see the OG render script).
const OG_PAGES = new Set([
  'services',
  'principal',
  'contact',
  'blog',
  'fractional-cto',
  'ai-consulting',
  'custom-software-development',
  'digital-transformation',
  'orlando',
  'central-florida',
]);

interface MetaTagsProps {
  title: string;
  description: string;
  image?: string;
  url?: string;
  type?: 'blog' | 'default';
}

export function MetaTags({ title, description, image, url, type = 'default' }: MetaTagsProps) {
  useEffect(() => {
    // Only add suffix if it's not already included
    document.title = title.includes('7Sigma') ? title : `${title} | 7Sigma Partners`;

    // Handle canonical URL
    const canonicalUrl = url || window.location.href;
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);

    // Use provided image or fall back to defaults
    let ogImageUrl = image;

    if (!ogImageUrl) {
      const baseUrl = window.location.origin;
      // Use blog preview for blog pages, default for everything else
      const seg =
        typeof window !== 'undefined'
          ? window.location.pathname.replace(/^\//, '').split('/')[0]
          : '';
      ogImageUrl = OG_PAGES.has(seg)
        ? `${baseUrl}/og/${seg}.png`
        : type === 'blog'
          ? `${baseUrl}${BLOG_OG_IMAGE}`
          : `${baseUrl}${DEFAULT_OG_IMAGE}`;
    } else if (!ogImageUrl.startsWith('http')) {
      // Ensure relative URLs become absolute
      const baseUrl = window.location.origin;
      ogImageUrl = `${baseUrl}${ogImageUrl}`;
    }
    // If it's already a full URL (like from Hashnode CDN), use it as-is

    // Determine the OG title based on page type
    let ogTitle = title;
    // For homepage, use tagline
    if (title.includes('Tech Leadership That Ships')) {
      ogTitle = '7Sigma - The Future is Fractional';
    }
    // For blog listing page
    else if (title === 'Blog - 7Sigma') {
      ogTitle = '7Sigma Insights';
    }
    // For other pages, just use the title as is (it already includes page name)
    // Blog posts will show their actual title
    // Series will show "Series Name - Blog Series"
    // Contact will show "Talk to 7Sigma | Schedule a Call" etc.

    const metaTags = {
      description,
      'og:title': ogTitle,
      'og:description': description,
      'og:type': type === 'blog' ? 'article' : 'website',
      'og:image': ogImageUrl,
      'og:url': canonicalUrl,
      'twitter:card': 'summary_large_image',
      'twitter:title': ogTitle,
      'twitter:description': description,
      'twitter:site': '@7sigmapr',
      'twitter:creator': '@7sigmapr',
      'twitter:image': ogImageUrl,
      keywords:
        'digital transformation, tech advisory, AI consulting, cloud solutions, blockchain development, product strategy',
      robots: 'index, follow',
      author: '7Sigma',
      language: 'en',
    };

    Object.entries(metaTags).forEach(([name, content]) => {
      let element = document.querySelector(
        `meta[${name.startsWith('og:') ? 'property' : 'name'}="${name}"]`
      );
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(name.startsWith('og:') ? 'property' : 'name', name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    });

    return () => {
      document.title = '7Sigma';
    };
  }, [title, description, image, url, type]);

  return null;
}
